import { useEffect, useRef, useState } from 'react'

interface HelpTooltipProps {
  text: string
  title?: string
}

export default function HelpTooltip({ text, title }: HelpTooltipProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!open) {
      return
    }

    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node | null
      if (!target) {
        return
      }
      if (!containerRef.current?.contains(target)) {
        setOpen(false)
      }
    }

    window.addEventListener('click', handleClick)
    return () => window.removeEventListener('click', handleClick)
  }, [open])

  return (
    <span ref={containerRef} className="group relative inline-flex items-center">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          setOpen((current) => !current)
        }}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 bg-slate-100 text-xs font-semibold text-slate-600 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        aria-label={title ? `${title} help` : 'Help'}
      >
        ?
      </button>

      <div
        className={`pointer-events-none absolute left-1/2 top-full z-30 mt-2 w-64 -translate-x-1/2 rounded-lg border border-slate-200 bg-white p-3 text-left text-xs text-slate-700 shadow-lg transition-opacity ${
          open ? 'pointer-events-auto opacity-100' : 'opacity-0 group-hover:pointer-events-auto group-hover:opacity-100'
        }`}
        role="tooltip"
      >
        {title ? <p className="mb-1 font-semibold text-slate-900">{title}</p> : null}
        <p>{text}</p>
      </div>
    </span>
  )
}
